import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import type { ProductAccessLevels } from '../../Team/types'

import { GetTeamMembersForChannel } from './useTeamMembersForChannel'

export const UpdateChannelMemberPermissions = graphql(/* GraphQL */ `
  mutation UpdateChannelMemberPermissions(
    $input: UpdateChannelMemberPermissionsInput!
  ) {
    updateChannelMemberPermissions(input: $input) {
      ... on UpdateChannelMemberPermissionsSuccess {
        updatedCount
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface MemberChannelProductPermission {
  accountId: string
  productAccessLevels: ProductAccessLevels
}

interface UpdateChannelMemberPermissionsInput {
  organizationId: string
  channelId: string
  /** Per-product member permissions, sent verbatim. */
  productMemberPermissions: MemberChannelProductPermission[]
}

interface UseUpdateChannelMemberPermissionsReturn {
  updateChannelMemberPermissions: (
    input: UpdateChannelMemberPermissionsInput,
  ) => Promise<{
    success: boolean
    updatedCount?: number
    error?: string
  }>
  loading: boolean
}

export const useUpdateChannelMemberPermissions =
  (): UseUpdateChannelMemberPermissionsReturn => {
    const [mutate, { loading }] = useMutation(UpdateChannelMemberPermissions)

    const updateChannelMemberPermissions = async (
      input: UpdateChannelMemberPermissionsInput,
    ): Promise<{ success: boolean; updatedCount?: number; error?: string }> => {
      // The backend persists the full per-product object and removes the
      // member's access to the channel when every product is `none`.
      const memberPermissions = input.productMemberPermissions.map(
        (member) => ({
          accountId: member.accountId,
          productAccessLevels: {
            publish: member.productAccessLevels.publish,
            comments: member.productAccessLevels.comments,
          },
        }),
      )

      try {
        const { data } = await mutate({
          variables: {
            input: {
              organizationId: input.organizationId,
              channelId: input.channelId,
              memberPermissions,
            },
          },
          refetchQueries: [
            {
              query: GetTeamMembersForChannel,
              variables: {
                input: {
                  organizationId: input.organizationId,
                  channelId: input.channelId,
                },
              },
            },
          ],
        })

        if (!data?.updateChannelMemberPermissions) {
          return {
            success: false,
            error: 'Failed to update channel member permissions',
          }
        }

        const result = data.updateChannelMemberPermissions

        if (result.__typename === 'UpdateChannelMemberPermissionsSuccess') {
          return { success: true, updatedCount: result.updatedCount }
        }

        // Handle error responses
        if (result.__typename === 'NotFoundError') {
          return { success: false, error: result.message }
        }

        return { success: false, error: 'Unknown error occurred' }
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'An error occurred',
        }
      }
    }

    return {
      updateChannelMemberPermissions,
      loading,
    }
  }
